/**
 * useServiceWorkerInit Hook
 *
 * Explicitly registers the Firebase Messaging service worker on app load.
 * This ensures the service worker stays registered for reliable push notifications
 * even when the page is closed.
 *
 * Why this is needed:
 * - Firebase's getToken() auto-registers the SW, but only when called
 * - If the SW becomes unregistered (browser GC, cache clear), it won't re-register
 *   until the user toggles notifications again
 * - By explicitly registering on every page load, we ensure the SW is always ready
 *
 * Usage:
 *   // Call once in AuthenticatedLayout or App root
 *   useServiceWorkerInit();
 */

import { useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

interface ServiceWorkerInitOptions {
  /** Whether to enable the hook (default: true) */
  enabled?: boolean;
}

export const useServiceWorkerInit = (options: ServiceWorkerInitOptions = {}) => {
  const { enabled = true } = options;
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only run once per app session
    if (!enabled || hasInitialized.current) {
      return;
    }

    const initServiceWorker = async () => {
      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        logger.log('[SW] Service workers not supported in this browser');
        return;
      }

      try {
        // Check if already registered
        const existingRegistrations = await navigator.serviceWorker.getRegistrations();
        const existingSW = existingRegistrations.find(
          (reg) => reg.active?.scriptURL.includes('firebase-messaging-sw.js')
        );

        if (existingSW) {
          logger.log('[SW] Firebase messaging service worker already registered:', existingSW.scope);

          // Trigger update check to ensure we have the latest SW
          existingSW.update().catch((err) => {
            logger.warn('[SW] Update check failed:', err);
          });

          hasInitialized.current = true;
          return;
        }

        // Register the service worker
        const registration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
          { scope: '/' }
        );

        logger.log('[SW] Firebase messaging service worker registered successfully');
        logger.log('[SW] Scope:', registration.scope);
        logger.log('[SW] State:', registration.active?.state || registration.installing?.state || 'installing');

        // Listen for state changes during installation
        if (registration.installing) {
          registration.installing.addEventListener('statechange', (e) => {
            const sw = e.target as ServiceWorker;
            logger.log('[SW] State changed to:', sw.state);
          });
        }

        hasInitialized.current = true;
      } catch (error) {
        logger.error('[SW] Service worker registration failed:', error);

        // Don't throw - notifications might still work via Firebase's auto-registration
        // This is a best-effort enhancement
      }
    };

    initServiceWorker();
  }, [enabled]);
};

export default useServiceWorkerInit;
